<template>
  <div class="category-manager">
    <!-- Add New Category -->
    <div class="add-category-form">
      <IntercomInput
        v-model="newCategoryName"
        :placeholder="$t('expense.newCategoryName')"
        class="category-input"
      />
      <IntercomButton variant="primary" size="sm" @click="handleCreate" :disabled="!newCategoryName.trim()">
        {{ $t('common.add') }}
      </IntercomButton>
    </div>

    <!-- Category List -->
    <div class="category-list">
      <div v-if="categories.length === 0" class="empty-state">
        {{ $t('expense.noCategories') }}
      </div>
      <div v-else>
        <div v-for="category in categories" :key="category.id" class="category-item">
          <template v-if="editingId === category.id">
            <IntercomInput
              v-model="editingName"
              class="category-input"
              @keyup.enter="handleUpdate(category.id)"
              @keyup.escape="cancelEdit"
            />
            <div class="category-actions">
              <IntercomButton variant="primary" size="sm" @click="handleUpdate(category.id)">
                {{ $t('common.save') }}
              </IntercomButton>
              <IntercomButton variant="ghost" size="sm" @click="cancelEdit">
                {{ $t('common.cancel') }}
              </IntercomButton>
            </div>
          </template>
          <template v-else>
            <span class="category-name">{{ category.name }}</span>
            <div class="category-actions">
              <IntercomButton variant="ghost" size="sm" @click="startEdit(category)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </IntercomButton>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Close Button -->
    <div class="manager-actions">
      <IntercomButton variant="secondary" @click="$emit('close')">
        {{ $t('common.done') }}
      </IntercomButton>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IntercomInput,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  categories: { type: Array, default: () => [] },
})

const emit = defineEmits(['create', 'update', 'close'])

// State
const newCategoryName = ref('')
const editingId = ref(null)
const editingName = ref('')

const handleCreate = () => {
  if (!newCategoryName.value.trim()) return
  emit('create', { name: newCategoryName.value.trim() })
  newCategoryName.value = ''
}

const startEdit = (category) => {
  editingId.value = category.id
  editingName.value = category.name
}

const cancelEdit = () => {
  editingId.value = null
  editingName.value = ''
}

const handleUpdate = (id) => {
  if (!editingName.value.trim()) return
  emit('update', id, { name: editingName.value.trim() })
  cancelEdit()
}
</script>

<style scoped>
.category-manager {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.add-category-form {
  display: flex;
  gap: var(--space-3);
}

.category-input {
  flex: 1;
}

.category-list {
  max-height: 300px;
  overflow-y: auto;
}

.empty-state {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-6);
}

.category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3);
  border-bottom: 1px solid var(--color-gray-100);
}

.category-item:last-child {
  border-bottom: none;
}

.category-name {
  color: var(--color-charcoal);
}

.category-actions {
  display: flex;
  gap: var(--space-2);
}

.manager-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
